import type { GameResponse, GameResult } from '@gamble-chess/contracts';
import type { JSX } from 'react';

import { useGames } from '../api/games.ts';
import { formatDateTime, platformLabels, resultLabels, timeClassLabels } from '../format.ts';
import { Badge, type BadgeTone } from '../ui/Badge.tsx';
import { Card } from '../ui/Card.tsx';
import { Entry, EntryList } from '../ui/EntryList.tsx';
import { Loaded } from '../ui/Loaded.tsx';
import { hintClass, linkClass } from '../ui/styles.ts';

// Colour only reinforces the result; the word itself is always shown.
const resultTones: Readonly<Record<GameResult, BadgeTone>> = {
  win: 'positive',
  draw: 'neutral',
  loss: 'negative',
};

function GameEntry({ game }: { readonly game: GameResponse }): JSX.Element {
  return (
    <Entry
      lead={`${game.player} vs ${game.opponent}`}
      meta={
        <span className={hintClass}>
          <a href={game.url} className={linkClass} rel="noreferrer">
            {formatDateTime(game.playedAt)}
          </a>
          {` · ${platformLabels[game.platform]} · ${timeClassLabels[game.timeClass]}${game.rated ? '' : ', casual'} · `}
          <span className="tabular-nums">
            {game.playerRating} vs {game.opponentRating}
          </span>
          {` · played ${game.color}`}
        </span>
      }
      trailing={<Badge tone={resultTones[game.result]}>{resultLabels[game.result]}</Badge>}
    />
  );
}

/** Every recorded game of every tracked account, newest first. */
export function RecentGames(): JSX.Element {
  const games = useGames();

  return (
    <Card title="Games recorded" description="What the poller has seen on your tracked accounts.">
      <Loaded
        query={games}
        loading="Loading games…"
        error="Your games could not be loaded. Please reload the page."
      >
        {(list) =>
          list.length === 0 ? (
            <p className={hintClass}>No games yet.</p>
          ) : (
            <EntryList label="Games">
              {list.map((game) => (
                <GameEntry key={game.id} game={game} />
              ))}
            </EntryList>
          )
        }
      </Loaded>
    </Card>
  );
}
